import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIOptimisticNanoProgress from 'UIComponents/progress/UIOptimisticNanoProgress';
import { useGetSelectedEnrollmentDetails } from 'automation-ui-canvas/containers/enrollment-history/panel/hooks/selectedEnrollmentHooks';
import SelectedEnrollmentHeader from 'automation-ui-canvas/containers/enrollment-history/panel/selected-enrollment/SelectedEnrollmentHeader';
import SelectedEnrollmentLogEventsSection from 'automation-ui-canvas/containers/enrollment-history/panel/selected-enrollment/SelectedEnrollmentLogEventsSection';
import { reportError } from 'automation-ui-core/errors/utils/reportError';
import GenericErrorMessage from 'automation-ui-log-events/components/GenericErrorMessage';
import styled from 'styled-components';
import { EnrollmentStates, ExecutionStates } from 'automation-ui-log-events/constants/LogEventConstants';
import { useIsUngated } from 'automation-ui-core/hooks/authHooks';
import { INTERACTIVE_ACTION } from 'automation-ui-enrollment/constants/Gates';
import { USER_INPUT_ACTION_TYPE_ID } from 'automation-ui-enrollment/constants/flowInteraction';
import { setScrollToActionCardState } from 'automation-ui-canvas/utils/routingUtils';
import { H6 } from 'foundations-components/transitional/components/H6';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const POLLING_INTERVAL_MS = 3000;
const StyledLogsTitle = styled.div.withConfig({
  displayName: "SelectedEnrollmentDetails__StyledLogsTitle",
  componentId: "sc-59w1an-0"
})(["background-color:", ";border-top:", " solid ", ";height:40px;padding-top:", ";"], ({
  theme
}) => theme.color['fill-field-default'], ({
  theme
}) => theme.borderWidth['100'], ({
  theme
}) => theme.color['border-core-default'], ({
  theme
}) => theme.space['250']);
const SelectedEnrollmentDetails = ({
  enrollmentId,
  isHybrid,
  flow,
  scrollContainer
}) => {
  var _lastLogEvent$action, _lastLogEvent$action2;
  const {
    flowId
  } = flow;
  const [polling, setPolling] = useState(false);
  const {
    loading,
    sortedLogEvents,
    refetchLogEvents,
    currentVersionFlow,
    error,
    startPolling,
    stopPolling
  } = useGetSelectedEnrollmentDetails({
    enrollmentId,
    flowId,
    isHybrid
  });
  const isUngatedToInteractiveAction = useIsUngated(INTERACTIVE_ACTION);
  let userInputEvent;
  const lastLogEvent = sortedLogEvents[sortedLogEvents.length - 1];
  if (isUngatedToInteractiveAction && (lastLogEvent === null || lastLogEvent === void 0 || (_lastLogEvent$action = lastLogEvent.action) === null || _lastLogEvent$action === void 0 ? void 0 : _lastLogEvent$action.actionTypeId) === USER_INPUT_ACTION_TYPE_ID && lastLogEvent.executionState === ExecutionStates.ASYNC) {
    userInputEvent = lastLogEvent;
  }
  const isCompleted = sortedLogEvents.some(logEvent => [EnrollmentStates.COMPLETED, EnrollmentStates.UNENROLLED, EnrollmentStates.FAILED].includes(logEvent.enrollmentState));
  const history = useHistory();
  const lastActionId = lastLogEvent === null || lastLogEvent === void 0 || (_lastLogEvent$action2 = lastLogEvent.action) === null || _lastLogEvent$action2 === void 0 ? void 0 : _lastLogEvent$action2.actionId;
  useEffect(() => {
    if (lastActionId && !isCompleted) {
      setScrollToActionCardState(lastActionId, history);
    }
  }, [lastActionId, isCompleted, history, scrollContainer]);
  useEffect(() => {
    if (polling) {
      var _scrollContainer$curr;
      (_scrollContainer$curr = scrollContainer.current) === null || _scrollContainer$curr === void 0 || _scrollContainer$curr.scrollTo({
        left: 0,
        top: scrollContainer.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [lastLogEvent, polling, scrollContainer]);
  useEffect(() => {
    setPolling(isUngatedToInteractiveAction && !isCompleted && !userInputEvent);
  }, [isUngatedToInteractiveAction, isCompleted, userInputEvent]);
  useEffect(() => {
    if (polling) {
      var _scrollContainer$curr2;
      startPolling(POLLING_INTERVAL_MS);
      (_scrollContainer$curr2 = scrollContainer.current) === null || _scrollContainer$curr2 === void 0 || _scrollContainer$curr2.scrollTo({
        left: 0,
        top: scrollContainer.current.scrollHeight,
        behavior: 'smooth'
      });
    }
    return stopPolling;
  }, [polling, startPolling, stopPolling, scrollContainer]);
  if (loading || !currentVersionFlow) {
    return /*#__PURE__*/_jsx(UIOptimisticNanoProgress, {
      value: 10
    });
  }
  if (error) {
    return /*#__PURE__*/_jsx(GenericErrorMessage, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation.enrollmentHistory.panel.enrollment-list.loadingError"
      }),
      children: /*#__PURE__*/_jsx(UIButton, {
        use: "tertiary-light",
        onClick: () => {
          refetchLogEvents().catch(err => {
            reportError({
              error: err
            });
          });
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation.enrollmentHistory.panel.enrollment-list.tryAgain"
        })
      })
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(SelectedEnrollmentHeader, {
      flow: flow,
      enrollmentLogEvent: sortedLogEvents[0],
      isHybrid: isHybrid
    }), /*#__PURE__*/_jsx(StyledLogsTitle, {
      children: /*#__PURE__*/_jsx(H6, {
        margin: "0",
        paddingInline: "1000",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation.enrollmentHistory.panel.selected-enrollment.logsForThisEnrollmentTitle"
        })
      })
    }), /*#__PURE__*/_jsx(SelectedEnrollmentLogEventsSection, {
      logEvents: sortedLogEvents,
      currentVersionFlow: currentVersionFlow,
      polling: polling,
      setPolling: setPolling,
      userInputEvent: userInputEvent
    })]
  });
};
export default SelectedEnrollmentDetails;